import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosConfig';
import './Master.css'; // Updated import
import { FaArrowUp, FaArrowDown, FaCommentAlt, FaTimes, FaPlay } from 'react-icons/fa';
import { FiShare } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ReplyCount from './ReplyCount';
import SharingComponent from './SharingComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import {NearMeSharp} from "@mui/icons-material";

// Video Player Component for Feed
const FeedVideoPlayer = ({ video }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  if (!video) return null;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div className="feed-video-container">
      {!isPlaying ? (
        <div className="feed-video-thumbnail" onClick={handlePlay}>
          <LazyLoadImage
            src={video.thumbnail_url}
            alt="Video thumbnail"
            className="feed-video-thumbnail-image"
            effect="blur"
            onError={(e) => (e.target.src = '/placeholder-image.png')}
          />
          <div className="feed-video-play-overlay">
            <FaPlay className="feed-play-icon" />
            <div className="feed-video-duration">
              {Math.round(video.duration)}s
            </div>
          </div>
        </div>
      ) : (
        <video
          controls
          autoPlay
          src={video.video_url}
          poster={video.thumbnail_url}
          className="feed-video-player"
          onError={(e) => console.error('Video failed to load:', e)}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

// Image Gallery Component for Feed
const FeedImageGallery = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="feed-image-gallery">
      {images.length === 1 ? (
        <div className="feed-single-image-container">
          <LazyLoadImage
            src={images[0].image_url}
            alt="Post attachment"
            className="feed-single-image"
            effect="blur"
            onClick={() => openLightbox(0)}
            onError={(e) => (e.target.src = '/placeholder-image.png')}
          />
        </div>
      ) : (
        <div className={`feed-image-grid ${images.length === 2 ? 'two-images' : images.length === 3 ? 'three-images' : 'four-plus-images'}`}>
          {images.slice(0, 4).map((image, index) => (
            <div key={image.id} className="feed-image-container" onClick={() => openLightbox(index)}>
              <LazyLoadImage
                src={image.image_url}
                alt={`Post attachment ${index + 1}`}
                className="feed-grid-image"
                effect="blur"
                onError={(e) => (e.target.src = '/placeholder-image.png')}
              />
              {index === 3 && images.length > 4 && (
                <div className="feed-more-images-overlay">
                  +{images.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div className="feed-lightbox-overlay" onClick={closeLightbox}>
          <div className="feed-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="feed-lightbox-close" onClick={closeLightbox}>
              <FaTimes />
            </button>

            <img src={images[currentImageIndex].image_url} alt="Enlarged view" />

            {images.length > 1 && (
              <>
                <button className="feed-lightbox-nav prev" onClick={prevImage}>
                  ←
                </button>
                <button className="feed-lightbox-nav next" onClick={nextImage}>
                  →
                </button>
                <div className="feed-lightbox-counter">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FeedPost = ({ post, onVote }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getClubUrl = () => {
    if (post.clubDetails?.slug) {
      return `/club/${post.clubDetails.slug}`;
    } else if (post.clubDetails?.club_id) {
      return `/club/${post.clubDetails.club_id}`;
    } else if (post.course_id) {
      return `/course/${post.course_id}`;
    }
    return '#';
  };

  return (
    <div className="feed-post">
      <div className="post-header">
        <div className="course-details-feed">
          <h2 className="course-name">
            <Link to={getClubUrl()} className="course-link">
              {post.clubDetails?.name || post.courseDetails?.golf_course || 'Loading...'}
            </Link>
          </h2>
        </div>
        <div className="course-location">
          <NearMeSharp />
          <span>
            {post.clubDetails?.city || post.courseDetails?.city}, <span className="state-abbreviation">
              {(post.clubDetails?.state || post.courseDetails?.state)?.toUpperCase()}
            </span>
          </span>
        </div>
      </div>
      <Link to={`/post/${post.id}`} className="message-title">
        <h3 className="post-title">{post.title}</h3>
      </Link>
      <div className="post-meta">
        <Link to={`/memberprofile/${post.username}`} className="username-link">
          <strong>{post.username}</strong>
        </Link>
          • {new Date(post.created_at).toLocaleDateString()}
      </div>
      <p className="post-content">
        {post.content.length > 100
          ? `${post.content.slice(0, 100)}...`
          : post.content}
      </p>

      {post.video ? (
        <FeedVideoPlayer video={post.video} />
      ) : post.images && post.images.length > 0 ? (
        <FeedImageGallery images={post.images} />
      ) : null}

      <div className="post-footer">
        <div className="voting-section">
          <FaArrowUp className="upvote" onClick={() => onVote(post.id, 'upvote')} />
          <span>{post.upvotes}</span>
          <FaArrowDown className="downvote" onClick={() => onVote(post.id, 'downvote')} />
          <span>{post.downvotes}</span>
        </div>
        <div className="replies-button">
          <Link to={`/post/${post.id}`} >
            <ReplyCount messageId={post.id} />
          </Link>
        </div>
        <div
          className="interaction-buttons"
          onClick={() => setIsModalOpen(true)}
          style={{ cursor: 'pointer' }}
        >
          <FiShare className="share-icon" />
        </div>
      </div>
      {isModalOpen && (
        <SharingComponent
          postUrl={`${window.location.origin}/post/${post.id}`}
          postTitle={`Check out this post: ${post.title}`}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [sortOption, setSortOption] = useState('mostRecent');
  const [stateOptions, setStateOptions] = useState([{ label: 'All States', value: '' }]);

  useEffect(() => {
    loadStates();
    loadPosts(true);
  }, [stateFilter, sortOption]);

  const loadStates = async () => {
    try {
      const response = await axiosInstance.get('/messageboard/messageboard/states/');
      const uniqueStates = Array.from(new Set(response.data.states.map((state) => state.toUpperCase()))).sort();
      const states = uniqueStates.map((state) => ({
        label: state,
        value: state,
      }));
      setStateOptions([{ label: 'All States', value: '' }, ...states]);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadPosts = async (resetPosts = false) => {
    if (loading) return;

    try {
      setLoading(true);
      const currentPage = resetPosts ? 1 : page;

      const response = await axiosInstance.get('/messageboard/messageboard/feed/', {
        params: {
          page: currentPage,
          state: stateFilter,
          sort: sortOption,
        },
      });

      const fetchedPosts = response.data.results;

      if (resetPosts) {
        setPosts(fetchedPosts);
        setPage(2);
      } else {
        setPosts((prevPosts) => [...prevPosts, ...fetchedPosts]);
        setPage((prev) => prev + 1);
      }

      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Error loading posts:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (postId, type) => {
    try {
      const endpoint = `/messageboard/messages/${postId}/${type}/`;
      const response = await axiosInstance.post(endpoint);

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId
            ? { ...post, upvotes: response.data.upvotes, downvotes: response.data.downvotes }
            : post
        )
      );
    } catch (error) {
      console.error(`Error ${type}voting on post ${postId}:`, error);
    }
  };

  const sortOptions = [
    { label: 'Date - Recent', value: 'mostRecent' },
    { label: 'Most Popular', value: 'mostPopular' },
  ];

  return (
    <div className="feed-wrapper">
      <div className="feed-post-container">
        <div className="feed-container messageboard-content">
          <div className="filter-section-feed">
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="state-filter-dropdown-feed"
              disabled={loading}
            >
              {stateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="sort-filter-dropdown-feed"
              disabled={loading}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <InfiniteScroll
            dataLength={posts.length}
            next={() => loadPosts(false)}
            hasMore={hasMore}
            scrollableTarget="feed-container"
            style={{ overflow: 'visible', width: '100%' }}
            endMessage={<p style={{textAlign: 'center', padding: '20px'}}>No more posts to load.</p>}
          >
            {posts.map((post) => (
              <FeedPost key={post.id} post={post} onVote={handleVote} />
            ))}
          </InfiniteScroll>
          {hasMore && !loading && (
            <button
              className="load-more"
              onClick={() => loadPosts(false)}
              style={{display: 'block', margin: '20px auto'}}
            >
              Load More
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;